import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Archive, CheckCircle2, Eye } from "lucide-react";

import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useArchiveFeeRule } from "@/features/fee-engine/api/use-archive-fee-rule";
import type { FeeRuleRow } from "@/features/fee-engine/components/fee-rules/fee-rules-columns";
import { FeeRuleActivateDialog } from "@/features/fee-engine/components/fee-rules/fee-rule-activate-dialog";
import { useSubmitApprovalRequest } from "@/features/shared/api/use-submit-approval-request";

/** Payload FEE_RULE_CHANGE : les valeurs tarifaires de la règle à activer. */
const feeRuleChangePayload = (feeRule: FeeRuleRow): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries({
      percentageValue: feeRule.percentageValue,
      fixedValueMinor: feeRule.fixedValueMinor,
      minFeeMinor: feeRule.minFeeMinor,
      maxFeeMinor: feeRule.maxFeeMinor,
      currency: feeRule.currency,
      effectiveFrom: feeRule.effectiveFrom,
      effectiveTo: feeRule.effectiveTo,
    }).filter(([, value]) => value != null && value !== "")
  );

/**
 * Menu d'actions de ligne pour les règles de frais.
 * L'activation est une action protégée (guide §5.4) : le dialog crée une
 * demande d'approbation FEE_RULE_CHANGE (maker-checker) — l'activation
 * réelle (`/activate?approvalRequestId=`) est exécutée par l'approbateur
 * depuis la page détail de la demande (`/app/admin/approvals/{id}`).
 */
export function FeeRuleRowActions({
  feeRule,
  navigate,
}: {
  feeRule: FeeRuleRow;
  navigate: NavigateFunction;
}) {
  const submitApproval = useSubmitApprovalRequest();
  const archive = useArchiveFeeRule();
  const [activateOpen, setActivateOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            id: "view",
            label: t("fees.rules.actions.view"),
            icon: Eye,
            // Pas de page détail dédiée en phase 5 — placeholder de navigation
            // conservé pour cohérence avec les autres features.
            onClick: () => {
              // Le coordinateur ajoutera la route détail si nécessaire.
            },
          },
          ...(feeRule.status !== "ACTIVE"
            ? [
                {
                  id: "activate",
                  label: t("fees.rules.actions.activate"),
                  icon: CheckCircle2,
                  onClick: () => setActivateOpen(true),
                },
              ]
            : []),
          ...(feeRule.status !== "ARCHIVED"
            ? [
                {
                  id: "archive",
                  label: t("fees.rules.actions.archive"),
                  icon: Archive,
                  variant: "destructive" as const,
                  onClick: () => setArchiveOpen(true),
                },
              ]
            : []),
        ]}
      />

      <FeeRuleActivateDialog
        feeRule={feeRule}
        open={activateOpen}
        onOpenChange={setActivateOpen}
        onConfirm={(reason) =>
          submitApproval.mutate(
            {
              actionType: "FEE_RULE_CHANGE",
              sourceService: "fee-engine-service",
              resourceType: "FeeRule",
              resourceId: feeRule.id,
              payload: feeRuleChangePayload(feeRule),
              reason,
            },
            {
              onSuccess: () => {
                notifSilence(t("fees.rules.feedback.approvalRequested"));
                setActivateOpen(false);
                navigate("/app/admin/approvals");
              },
              onError: (error) => notifSilenceError(error.message),
            }
          )
        }
        pending={submitApproval.isPending}
      />

      <MutationConfirmPreview
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        data={{
          id: feeRule.id,
          productCode: feeRule.productCode,
          status: feeRule.status,
        }}
        title={t("common.mutationPreview.deleteTitle")}
        description={t("common.mutationPreview.statusDescription")}
        confirmLabel={t("fees.rules.actions.archive")}
        t={t}
        pending={archive.isPending}
        onConfirm={() =>
          archive.mutate(feeRule.id, {
            onSuccess: () => {
              notifSilence(t("fees.rules.feedback.archived"));
              setArchiveOpen(false);
            },
            onError: (error) => notifSilenceError(error.message),
          })
        }
      />
    </>
  );
}
